import { useCheckPermission } from '@/hooks/useUserRoles';
import { useItemsProjectPermissions } from '@/hooks/useResourceProjectPermissions';
import { useUserRoles } from '@/hooks/useUserRoles';
import { logger } from '@/lib/logger';

export const useItemsReorderPermission = (projectId: string | undefined) => {
  const canReorderItems = useCheckPermission('ITEMS_REORDER');
  const { can_view: canViewProject, can_edit: canEditProject, isLoading: projectLoading } = useItemsProjectPermissions(projectId || '');
  const { data: userRoles, isLoading: rolesLoading } = useUserRoles();
  
  // Check if user is admin - admins can reorder regardless of other permissions
  const isAdmin = userRoles?.some(role => role.role_code === 'ROLE_ADMIN');
  
  // Determine if we're still loading
  const isLoading = rolesLoading || 
                   canReorderItems.isLoading ||
                   projectLoading;
  
  // Hybrid approach: Admin can always reorder
  // OR user needs BOTH:
  //   1. ITEMS_REORDER global permission
  //   2. ANY project relation (can_view or can_edit means they're in the project)
  const hasProjectRelation = canViewProject || canEditProject;
  const canReorder = isAdmin || 
                     ((canReorderItems.data === true) && hasProjectRelation);
  
  // Debug logging with enhanced detail
  const logData = {
    projectId,
    hasItemsReorderPermission: canReorderItems.data,
    hasProjectViewPermission: canViewProject,
    hasProjectEditPermission: canEditProject,
    hasProjectRelation,
    isAdmin,
    userRoles: userRoles?.map(r => r.role_code),
    finalCanReorder: canReorder,
    isLoading,
    loadingStates: {
      rolesLoading,
      itemsReorderLoading: canReorderItems.isLoading,
      projectLoading
    }
  };
  
  logger.debug('Reorder Permission Check:', logData);
  
  return { canReorder, isLoading };
};